"use client";

// The beta's email check (/beta). The visitor enters an email, Clerk emails
// six digits, and the digits make a Clerk session. The standalone beta does
// not read or create a TWT member or TWT session.
//
// Clerk's own email code does the work, through its sign-in and sign-up
// resources (@clerk/nextjs/legacy): a known address signs in, a new address
// signs up, and both verify the same six digits. None of TWT's sign-in routes
// (/api/login/*) take part, so the member sign-in hold is untouched.
//
// The screen is the sign-in modal's own markup and classes (the email step
// and the code step of EmailCodeSignIn.tsx, styled by SignInModalStyles.tsx)
// on the checkout sign-in's dark sheet with the T mark.

import { isClerkAPIResponseError } from "@clerk/nextjs/errors";
import { useAuth, useClerk } from "@clerk/nextjs";
import { useSignIn, useSignUp } from "@clerk/nextjs/legacy";
import { type FormEvent, useCallback, useEffect, useId, useRef, useState } from "react";
import { ClerkProviderBoundary } from "~/components/auth/ClerkProviderBoundary";
import { inboxFor } from "~/components/auth/inboxLink";
import { maskEmailForDisplay } from "~/components/auth/loginCodeClient";
import { OtpCodeInput } from "~/components/auth/OtpCodeInput";
import { SignInStepBackControl, SignInStepFrame } from "~/components/auth/SignInStepFrame";
import { SignInTermsLine } from "~/components/auth/SignInTermsLine";
import { AlertIcon, ButtonSpinner, ChevronLeftIcon, EnvelopeIcon } from "~/components/auth/signInModalIcons";
import { TwtBrandMark } from "~/components/TwtBrand";

const RESEND_COOLDOWN_SECONDS = 30;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const CODE_LENGTH = 6;

type Props = {
  /** Where the browser goes once its Clerk session exists. */
  next: string;
  accessIssue?: "verify_email" | "not_eligible" | null;
};

type Step = "email" | "code";
type Status = "idle" | "sending" | "verifying" | "finishing";

/** Which Clerk resource holds the attempt, and what a resend needs. */
type Flow = { kind: "signIn"; emailAddressId: string } | { kind: "signUp" };

/** The screen: the sheet's head, the panel, and the terms line. */
export function BetaSignIn({ next, accessIssue = null }: Props) {
  const [codeStep, setCodeStep] = useState(false);
  const titleId = useId();
  const onCodeStepChange = useCallback((value: boolean) => setCodeStep(value), []);
  return (
    <section
      className="twt-signin-modal twt-signin-screen"
      data-brand="mark"
      aria-labelledby={titleId}
    >
      <div className="twt-signin-screen-head">
        <span className="twt-signin-brand" role="img" aria-label="Trade with Titans">
          <TwtBrandMark className="twt-signin-brand-mark" />
        </span>
        <h1 id={titleId} className="whop-login-title">
          {codeStep ? "Enter code" : accessIssue === "not_eligible" ? "Use your member email" : accessIssue ? "Verify your email" : "Enter your email"}
        </h1>
        <p className="whop-login-subtext">
          {codeStep
            ? "Check your email for a one-time passcode."
            : accessIssue === "not_eligible" ? "This email isn’t on the beta access list."
            : accessIssue ? "Use a verified email to open the beta."
            : "Member beta: ES and NQ charts, Volume Profile and the DOM."}
        </p>
      </div>
      <ClerkProviderBoundary
        prefetchUI={false}
        fallback={<BetaEmailCodeUnavailable />}
      >
        <BetaEmailCode next={next} accessIssue={accessIssue} onCodeStepChange={onCodeStepChange} />
      </ClerkProviderBoundary>
      {codeStep ? null : <SignInTermsLine />}
    </section>
  );
}

/** The message a Clerk refusal carries, by its code. */
function clerkErrorCode(error: unknown): string {
  if (!isClerkAPIResponseError(error)) return "";
  return error.errors[0]?.code ?? "";
}

function clerkErrorMessage(error: unknown, fallback: string): string {
  if (!isClerkAPIResponseError(error)) return fallback;
  const first = error.errors[0];
  return first?.longMessage || first?.message || fallback;
}

function BetaEmailCode({ next, accessIssue = null, onCodeStepChange }: Props & { onCodeStepChange: (codeStep: boolean) => void }) {
  const { isSignedIn } = useAuth();
  const { signOut } = useClerk();
  const { isLoaded: signInLoaded, signIn, setActive } = useSignIn();
  const { isLoaded: signUpLoaded, signUp } = useSignUp();
  const fieldId = useId();
  const [step, setStep] = useState<Step>("email");
  const [email, setEmail] = useState("");
  const [sentEmail, setSentEmail] = useState("");
  const [code, setCode] = useState("");
  const [status, setStatus] = useState<Status>("idle");
  const [error, setError] = useState("");
  const [shake, setShake] = useState(false);
  const [resendSeconds, setResendSeconds] = useState(0);
  const [resendState, setResendState] = useState<"idle" | "sending" | "sent">("idle");
  /** Null once the code is spent or expired: Resend is the way on. */
  const [flow, setFlow] = useState<Flow | null>(null);

  const mountedRef = useRef(true);
  const statusRef = useRef<Status>("idle");
  statusRef.current = status;
  const flowRef = useRef<Flow | null>(null);
  flowRef.current = flow;
  const codeRef = useRef("");
  const submittedCodeRef = useRef("");
  // Bumped by every send and by going back, so a late answer to an older
  // request never paints over the step the visitor is on.
  const generationRef = useRef(0);
  const sendLockRef = useRef(false);
  const verifyLockRef = useRef(false);
  const codeInputRef = useRef<HTMLInputElement>(null);
  const emailInputRef = useRef<HTMLInputElement>(null);
  const codeHelpId = `${fieldId}-help`;
  const codeErrorId = `${fieldId}-error`;
  const clerkReady = signInLoaded && signUpLoaded && Boolean(signIn && signUp && setActive);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    onCodeStepChange(step === "code");
  }, [step, onCodeStepChange]);

  useEffect(() => {
    if (step === "code") codeInputRef.current?.focus();
  }, [step, flow]);

  useEffect(() => {
    if (resendSeconds <= 0) return;
    const timer = window.setTimeout(() => setResendSeconds((seconds) => Math.max(0, seconds - 1)), 1000);
    return () => window.clearTimeout(timer);
  }, [resendSeconds]);

  useEffect(() => {
    if (resendState !== "sent") return;
    const timer = window.setTimeout(() => setResendState("idle"), 4000);
    return () => window.clearTimeout(timer);
  }, [resendState]);

  useEffect(() => {
    if (!shake) return;
    codeInputRef.current?.focus();
    const timer = window.setTimeout(() => setShake(false), 450);
    return () => window.clearTimeout(timer);
  }, [shake]);

  /** Clerk emails a code for this address: sign-in for a known address, sign-up for a new one. */
  async function sendCode(address: string): Promise<Flow> {
    if (!signIn || !signUp) throw new Error("Sign-in is still loading.");
    try {
      const attempt = await signIn.create({ identifier: address });
      const factor = attempt.supportedFirstFactors?.find((candidate) => candidate.strategy === "email_code");
      if (!factor || factor.strategy !== "email_code") {
        throw new Error("This email cannot receive a sign-in code.");
      }
      await attempt.prepareFirstFactor({ strategy: "email_code", emailAddressId: factor.emailAddressId });
      return { kind: "signIn", emailAddressId: factor.emailAddressId };
    } catch (error) {
      if (clerkErrorCode(error) !== "form_identifier_not_found") throw error;
    }
    await signUp.create({ emailAddress: address });
    await signUp.prepareEmailAddressVerification({ strategy: "email_code" });
    return { kind: "signUp" };
  }

  /** A fresh code for the address the visitor already gave. */
  async function resendCode(current: Flow | null, address: string): Promise<Flow> {
    if (!signIn || !signUp) throw new Error("Sign-in is still loading.");
    if (current?.kind === "signIn") {
      await signIn.prepareFirstFactor({ strategy: "email_code", emailAddressId: current.emailAddressId });
      return current;
    }
    if (current?.kind === "signUp") {
      await signUp.prepareEmailAddressVerification({ strategy: "email_code" });
      return current;
    }
    return sendCode(address);
  }

  /** The six digits, checked against the attempt that sent them. True once the browser holds the session. */
  async function verifyCode(current: Flow, value: string): Promise<boolean> {
    if (!signIn || !signUp || !setActive) throw new Error("Sign-in is still loading.");
    if (current.kind === "signIn") {
      const result = await signIn.attemptFirstFactor({ strategy: "email_code", code: value });
      if (result.status !== "complete" || !result.createdSessionId) return false;
      await setActive({ session: result.createdSessionId });
      return true;
    }
    const result = await signUp.attemptEmailAddressVerification({ code: value });
    if (result.status !== "complete" || !result.createdSessionId) return false;
    await setActive({ session: result.createdSessionId });
    return true;
  }

  function finishedCode() {
    setFlow(null);
    setCode("");
    codeRef.current = "";
    submittedCodeRef.current = "";
    setResendSeconds(0);
  }

  async function submitCode(value: string, current: Flow) {
    if (value.length !== CODE_LENGTH || verifyLockRef.current) return;
    verifyLockRef.current = true;
    const generation = generationRef.current;
    setStatus("verifying");
    setError("");
    try {
      const signedIn = await verifyCode(current, value);
      if (!mountedRef.current || generation !== generationRef.current) return;
      if (!signedIn) {
        setStatus("idle");
        finishedCode();
        setError("Sign-in did not finish. Request a new code and try again.");
        return;
      }
      setStatus("finishing");
      window.location.assign(next);
    } catch (error) {
      if (!mountedRef.current || generation !== generationRef.current) return;
      setStatus("idle");
      setCode("");
      codeRef.current = "";
      submittedCodeRef.current = "";
      const code = clerkErrorCode(error);
      if (code === "form_code_incorrect") {
        setError("That code isn't right. Try again.");
        setShake(true);
      } else if (code === "verification_expired") {
        finishedCode();
        setError("That code has expired. Request a new one.");
      } else if (code === "verification_failed" || code === "too_many_requests") {
        finishedCode();
        setError("Too many tries. Request a new code.");
      } else if (code === "session_exists") {
        // The browser already holds a Clerk session: the workspace reads it.
        setStatus("finishing");
        window.location.assign(next);
        return;
      } else {
        setError(clerkErrorMessage(error, "We couldn't check that code. Try again."));
      }
    } finally {
      verifyLockRef.current = false;
    }
  }

  function updateCode(value: string) {
    const digits = value.replace(/\D/g, "").slice(0, CODE_LENGTH);
    setCode(digits);
    codeRef.current = digits;
    if (error && digits) setError("");
    if (digits.length < CODE_LENGTH) submittedCodeRef.current = "";
    const current = flowRef.current;
    if (digits.length === CODE_LENGTH && submittedCodeRef.current !== digits && current && statusRef.current === "idle") {
      submittedCodeRef.current = digits;
      void submitCode(digits, current);
    }
  }

  async function submitEmail(event: FormEvent) {
    event.preventDefault();
    if (!clerkReady || sendLockRef.current || statusRef.current !== "idle") return;
    const normalized = email.trim().toLowerCase();
    if (!EMAIL_PATTERN.test(normalized)) {
      setError("Enter a valid email address.");
      emailInputRef.current?.focus();
      return;
    }
    sendLockRef.current = true;
    const generation = ++generationRef.current;
    // The code step shows the moment Continue is pressed; digits typed while
    // the email goes out are kept and checked as soon as it has.
    setError("");
    setCode("");
    codeRef.current = "";
    submittedCodeRef.current = "";
    setFlow(null);
    flowRef.current = null;
    setSentEmail(normalized);
    setStatus("sending");
    setStep("code");
    try {
      const sent = await sendCode(normalized);
      if (!mountedRef.current || generation !== generationRef.current) return;
      setFlow(sent);
      flowRef.current = sent;
      setResendSeconds(RESEND_COOLDOWN_SECONDS);
      setStatus("idle");
      statusRef.current = "idle";
      const buffered = codeRef.current;
      if (buffered.length === CODE_LENGTH && submittedCodeRef.current !== buffered) {
        submittedCodeRef.current = buffered;
        void submitCode(buffered, sent);
      }
    } catch (error) {
      if (!mountedRef.current || generation !== generationRef.current) return;
      setStatus("idle");
      if (clerkErrorCode(error) === "session_exists") {
        setStatus("finishing");
        window.location.assign(next);
        return;
      }
      setStep("email");
      setError(clerkErrorMessage(error, "We couldn't email you a code. Please try again."));
      window.setTimeout(() => emailInputRef.current?.focus(), 0);
    } finally {
      sendLockRef.current = false;
    }
  }

  async function resend() {
    if (status !== "idle" || resendSeconds > 0 || resendState === "sending" || !sentEmail) return;
    const generation = ++generationRef.current;
    setResendState("sending");
    setError("");
    setCode("");
    codeRef.current = "";
    submittedCodeRef.current = "";
    try {
      const sent = await resendCode(flowRef.current, sentEmail);
      if (!mountedRef.current || generation !== generationRef.current) return;
      setFlow(sent);
      flowRef.current = sent;
      setResendState("sent");
      setResendSeconds(RESEND_COOLDOWN_SECONDS);
    } catch (error) {
      if (!mountedRef.current || generation !== generationRef.current) return;
      setResendState("idle");
      setError(clerkErrorMessage(error, "We couldn't send a new code. Please try again."));
    }
  }

  function backToEmail() {
    if (status === "verifying" || status === "finishing") return;
    generationRef.current += 1;
    sendLockRef.current = false;
    setStep("email");
    setStatus("idle");
    setFlow(null);
    flowRef.current = null;
    setCode("");
    codeRef.current = "";
    submittedCodeRef.current = "";
    setError("");
    setResendState("idle");
    setResendSeconds(0);
    window.setTimeout(() => emailInputRef.current?.focus(), 0);
  }

  const busy = status === "verifying" || status === "finishing";
  const sending = status === "sending";
  const sentTo = maskEmailForDisplay(sentEmail);
  const inbox = inboxFor(sentEmail);
  const codeFieldDisabled = busy || resendState === "sending" || (!flow && !sending);
  const progress = sending
    ? "Sending code"
    : status === "verifying"
    ? "Checking code"
    : status === "finishing"
    ? "Opening the beta"
    : "";

  const body =
    step === "code" ? (
      <form
        className="whop-oauth-form whop-oauth-code-form"
        onSubmit={(event) => {
          event.preventDefault();
          if (code.length === CODE_LENGTH && flow && status === "idle") {
            submittedCodeRef.current = code;
            void submitCode(code, flow);
          }
        }}
        aria-busy={busy || sending}
      >
        <SignInStepBackControl>
          <button type="button" className="twt-modal-back whop-oauth-switch" disabled={busy} onClick={backToEmail}>
            <ChevronLeftIcon />
            <span className="twt-visually-hidden">Use a different email</span>
          </button>
        </SignInStepBackControl>
        <div className="whop-oauth-code-head twt-visually-hidden">
          <p className="whop-oauth-code-sentto" id={codeHelpId}>
            {sending ? "Sending a 6-digit code to " : "Enter the 6-digit code sent to "}
            <span>{sentTo || "your email"}</span>.
          </p>
        </div>
        {error ? (
          <p className="whop-oauth-error whop-oauth-code-error" id={codeErrorId} role="alert">
            <AlertIcon />
            <span>{error}</span>
          </p>
        ) : null}
        <div
          className={`whop-oauth-code-wrap${shake ? " is-shaking" : ""}`}
          onAnimationEnd={(event) => {
            if (event.target === event.currentTarget) setShake(false);
          }}
        >
          <OtpCodeInput
            ref={codeInputRef}
            id={fieldId}
            value={code}
            onChange={updateCode}
            disabled={codeFieldDisabled}
            invalid={Boolean(error)}
            describedBy={`${codeHelpId}${error ? ` ${codeErrorId}` : ""}`}
            errorMessageId={codeErrorId}
          />
        </div>
        {inbox ? (
          <a className="twt-otp-inbox" href={inbox.href} target="_blank" rel="noopener noreferrer">
            <EnvelopeIcon size={17} />
            {inbox.label}
          </a>
        ) : null}
        {progress ? (
          <p className="whop-oauth-code-status" role="status" aria-live="polite">
            <ButtonSpinner />
            <span>{progress}</span>
          </p>
        ) : null}
        <p className="whop-oauth-resend">
          Didn&apos;t receive a code?{" "}
          <button
            type="button"
            onClick={() => void resend()}
            disabled={busy || sending || resendSeconds > 0 || resendState === "sending"}
            className="whop-oauth-resend-btn"
          >
            {resendState === "sending" ? "Sending..." : resendSeconds > 0 ? `Resend in ${resendSeconds}s` : "Resend"}
          </button>
          {resendState === "sent" ? (
            <span className="whop-oauth-resend-ok" role="status" aria-live="polite">
              New code sent.
            </span>
          ) : null}
        </p>
      </form>
    ) : (
      <form className="whop-oauth-form" onSubmit={submitEmail} noValidate>
        <div className="whop-oauth-secondary">
          <label className="whop-oauth-email-label" htmlFor={`${fieldId}-email`}>
            Email
          </label>
          <div className="whop-oauth-email-wrap">
            <EnvelopeIcon className="whop-oauth-email-icon" />
            <input
              ref={emailInputRef}
              id={`${fieldId}-email`}
              name="email"
              aria-label="Email address"
              aria-invalid={error ? true : undefined}
              aria-describedby={error ? `${fieldId}-email-error` : undefined}
              autoComplete="email"
              inputMode="email"
              autoCapitalize="none"
              spellCheck={false}
              value={email}
              disabled={!clerkReady}
              onChange={(event) => {
                setEmail(event.target.value);
                if (error) setError("");
              }}
              placeholder="name@example.com"
              type="email"
              className="whop-oauth-email-field"
            />
          </div>
        </div>
        {error ? (
          <p id={`${fieldId}-email-error`} className="whop-oauth-error" role="alert">
            <AlertIcon />
            <span>{error}</span>
          </p>
        ) : null}
        <div className="whop-oauth-actions">
          <button type="submit" disabled={!clerkReady} className="whop-oauth-approve">
            Continue
          </button>
        </div>
      </form>
    );

  if (accessIssue && isSignedIn) {
    return (
      <div className="whop-oauth-form twt-signin">
        <p className="whop-login-subtext">{accessIssue === "not_eligible" ? "Use the email linked to your Trade with Titans membership." : "This email has not been verified yet."}</p>
        <button type="button" className="whop-oauth-approve" onClick={() => void signOut({ redirectUrl: "/beta" })}>
          Use a different email
        </button>
      </div>
    );
  }

  return (
    <>
      <SignInStepFrame stepKey={step} direction={step === "code" ? "forward" : "back"} className="twt-signin">
        {body}
      </SignInStepFrame>
      {/* Keep Clerk's verification widget mounted while the email step changes. */}
      <div id="clerk-captcha" className="twt-signin-captcha" data-cl-theme="dark" />
      <BetaSignInStyles />
    </>
  );
}

/** Without Clerk's publishable key the email form stays visible and says sign-in is unavailable. */
function BetaEmailCodeUnavailable() {
  const fieldId = useId();
  return (
    <>
      <form className="whop-oauth-form twt-signin" noValidate onSubmit={(event) => event.preventDefault()}>
        <div className="whop-oauth-secondary">
          <label className="whop-oauth-email-label" htmlFor={`${fieldId}-email`}>
            Email
          </label>
          <div className="whop-oauth-email-wrap">
            <EnvelopeIcon className="whop-oauth-email-icon" />
            <input
              id={`${fieldId}-email`}
              name="email"
              aria-label="Email address"
              autoComplete="email"
              inputMode="email"
              disabled
              placeholder="name@example.com"
              type="email"
              className="whop-oauth-email-field"
            />
          </div>
        </div>
        <p className="whop-oauth-error" role="alert">
          <AlertIcon />
          <span>Sign-in is not available right now. Try again shortly.</span>
        </p>
        <div className="whop-oauth-actions">
          <button type="submit" disabled className="whop-oauth-approve">
            Continue
          </button>
        </div>
      </form>
      <BetaSignInStyles />
    </>
  );
}

/** The rules the sign-in panel scopes to itself and this screen needs too. */
function BetaSignInStyles() {
  return (
    <style jsx global>{`
      .twt-signin-screen .whop-oauth-code-form {
        width: 100%;
        max-width: 440px;
        gap: 18px;
      }
      .twt-signin-screen .twt-signin-captcha:empty {
        display: none;
      }
      .twt-signin-screen .twt-signin-captcha {
        align-self: center;
      }
      .twt-signin-screen .twt-otp-inbox {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        width: 100%;
        min-height: 44px;
        margin-top: -2px;
        border-radius: 10px;
        background: var(--twt-modal-provider-tile);
        box-shadow: inset 0 0 0 1px var(--twt-modal-provider-border);
        color: var(--twt-modal-text);
        font-size: var(--twt-text-body-size);
        line-height: var(--twt-text-body-line);
        font-weight: 500;
        text-decoration: none;
        transition: background 150ms ease, transform 120ms ease;
      }
      .twt-signin-screen .twt-otp-inbox:hover {
        background: var(--twt-modal-provider-hover);
      }
      .twt-signin-screen .twt-otp-inbox:active {
        transform: scale(0.99);
      }
      .twt-signin-screen .twt-otp-inbox:focus-visible {
        outline: 2px solid var(--twt-modal-accent);
        outline-offset: 3px;
      }
      @media (max-width: 380px) {
        .twt-signin-screen .whop-oauth-code-form {
          gap: 16px;
        }
      }
    `}</style>
  );
}
